/**
 * Superadmin — Broken Tracks Registry.
 *
 * Aggregates fleet-wide `handoff_skip_bad_track` + `handoff_silent`
 * telemetry into a shopping list of Apple track IDs to remove from
 * playlists. Loads once when the section mounts; a refresh button
 * re-fetches on demand.
 *
 * Design intent (Sep 2026): the "same song repeated" Yazu Bangalore
 * failure mode was traced to specific broken Apple IDs (deleted from
 * catalogue, DRM-locked in the venue's region, etc.). This tool
 * surfaces them so an admin can proactively clean up before the guest
 * notices.
 */
import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { Loader2, RefreshCw, Copy, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const relTime = (iso) => {
  if (!iso) return '—';
  const t = new Date(iso).getTime();
  if (!t) return '—';
  const diff = Math.max(0, Date.now() - t);
  const m = Math.floor(diff / 60000);
  if (m < 1) return 'just now';
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 48) return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
};

const BrokenTracksSection = () => {
  const [hours, setHours] = useState(168);
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/analytics/admin/broken-tracks`, {
        params: { hours, min_fails: 2 },
      });
      setData(res.data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to load broken tracks');
    } finally {
      setLoading(false);
    }
  }, [hours]);

  useEffect(() => { load(); }, [load]);

  const copyIds = () => {
    const ids = (data?.tracks || []).map((t) => t.apple_id).join('\n');
    if (!ids) return;
    navigator.clipboard.writeText(ids).then(
      () => toast.success(`Copied ${data.tracks.length} Apple IDs to clipboard`),
      () => toast.error('Clipboard blocked'),
    );
  };

  return (
    <section className="rounded-2xl border border-white/10 bg-white/[0.02] p-5" data-testid="broken-tracks-section">
      <header className="flex items-center justify-between gap-3 mb-4">
        <div className="flex items-center gap-2">
          <AlertTriangle className="w-5 h-5 text-[#FFB020]" />
          <h2 className="text-lg font-bold text-white" style={{ fontFamily: 'Outfit' }}>Broken tracks</h2>
          {data && (
            <span className="text-xs font-mono text-white/40" data-testid="broken-tracks-count">
              ({data.tracks?.length || 0})
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          <select
            data-testid="broken-tracks-hours"
            value={hours}
            onChange={(e) => setHours(Number(e.target.value))}
            className="bg-white/5 border border-white/10 rounded-lg px-3 py-1.5 text-sm text-white focus:outline-none focus:border-[#FF007F]"
          >
            <option value={24}>24h</option>
            <option value={168}>7d</option>
            <option value={720}>30d</option>
          </select>
          <button
            onClick={load}
            disabled={loading}
            data-testid="broken-tracks-refresh"
            className="w-8 h-8 flex items-center justify-center rounded-lg bg-white/5 hover:bg-white/10 text-white/70"
            title="Refresh"
          >
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
          </button>
          <button
            onClick={copyIds}
            disabled={!data?.tracks?.length}
            data-testid="broken-tracks-copy"
            className="flex items-center gap-1.5 text-xs font-bold uppercase tracking-widest px-3 py-1.5 rounded-lg bg-white/5 hover:bg-white/10 text-white disabled:opacity-40 disabled:cursor-not-allowed"
          >
            <Copy className="w-3 h-3" /> Copy IDs
          </button>
        </div>
      </header>

      <p className="text-xs text-white/50 mb-4 leading-snug">
        Apple track IDs that failed to hand off across venues in the selected window. Skip count = times the 3-strike skip fired; silent = individual hand-off failures. Remove these from playlists to stop the "same song repeating" complaint.
      </p>

      {loading && !data ? (
        <div className="flex items-center justify-center py-10">
          <Loader2 className="w-6 h-6 text-white/50 animate-spin" />
        </div>
      ) : !data?.tracks?.length ? (
        <div className="rounded-xl border border-dashed border-white/10 p-8 text-center">
          <p className="text-sm text-[#39FF14]">No broken tracks in this window.</p>
          <p className="text-xs text-white/40 mt-1">Fleet playback hand-off is clean. Nice.</p>
        </div>
      ) : (
        <div className="rounded-xl border border-white/10 overflow-hidden">
          <div className="grid grid-cols-[1fr_auto_auto_auto] gap-3 px-4 py-2 bg-white/[0.04] border-b border-white/10 text-[10px] uppercase tracking-widest text-white/40 font-semibold">
            <div>Track</div>
            <div className="text-right">Skips</div>
            <div className="text-right">Silent</div>
            <div className="text-right">Last</div>
          </div>
          <div className="max-h-[520px] overflow-y-auto divide-y divide-white/5">
            {data.tracks.map((t) => (
              <div key={t.apple_id} className="grid grid-cols-[1fr_auto_auto_auto] gap-3 px-4 py-3 items-center hover:bg-white/[0.03]" data-testid={`broken-track-${t.apple_id}`}>
                <div className="min-w-0">
                  <div className="flex items-baseline gap-2">
                    <span className="text-sm font-semibold text-white truncate">
                      {t.name || <span className="text-white/40">(name unresolved)</span>}
                    </span>
                    <span className="text-[11px] font-mono text-white/40 whitespace-nowrap">{t.apple_id}</span>
                  </div>
                  <div className="text-xs text-white/50 truncate">
                    {(t.artists || []).join(', ') || '—'}
                  </div>
                  <div className="flex flex-wrap gap-1 mt-1.5">
                    {(t.venues_affected || []).slice(0, 4).map((v) => (
                      <span key={v.host_id} className="text-[10px] px-2 py-0.5 rounded bg-[#FF007F]/10 text-[#FF007F] font-semibold">
                        {v.name || v.email}
                      </span>
                    ))}
                    {(t.venues_affected || []).length > 4 && (
                      <span className="text-[10px] px-2 py-0.5 rounded bg-white/5 text-white/60">
                        +{t.venues_affected.length - 4} more
                      </span>
                    )}
                    {(t.in_playlists || []).length > 0 && (
                      <span className="text-[10px] px-2 py-0.5 rounded bg-white/5 text-white/60" title={t.in_playlists.map((p) => p.name).join(', ')}>
                        in {t.in_playlists.length} playlist{t.in_playlists.length === 1 ? '' : 's'}
                      </span>
                    )}
                  </div>
                </div>
                <div className="text-right font-bold text-[#FF3B3B] tabular-nums min-w-[3ch]">{t.skip_count}</div>
                <div className="text-right font-mono text-[#FFB020] tabular-nums min-w-[3ch]">{t.silent_count}</div>
                <div className="text-right text-[11px] text-white/40 whitespace-nowrap min-w-[6ch]">{relTime(t.last_seen)}</div>
              </div>
            ))}
          </div>
        </div>
      )}
    </section>
  );
};

export default BrokenTracksSection;
